import React, { useMemo, createContext, useContext, useReducer, useEffect } from 'react';
import { useParams } from '@reach/router';
import { COLOR_PICKER } from '../constants';
import { SENTENCES } from '../constants/navigation';
import { ExemplarWord } from '../types';
import { UndoRedoContext } from './undoRedoContext';
import { Type, UndoRedoAction } from '../hooks/useUndoRedo';

export enum ActionTypes {
  SET_COLOR = 'SET_COLOR',
  SET_MODE = 'SET_MODE',
  SET_HIDE_HIGHLIGHTS_MODE = 'SET_HIDE_HIGHLIGHTS_MODE',
  SET_SENTENCES = 'SET_SENTENCES',
  SET_FONT_COLOR = 'SET_FONT_COLOR',
  SET_FONT = 'SET_FONT',
  SET_TEXT_COLOR = 'SET_TEXT_COLOR',
  SET_TOOL = 'SET_TOOL',
  SET_IMAGE = 'SET_IMAGE',
  SET_IMAGE_TOOL = 'SET_IMAGE_TOOL',
  SET_HIDDEN_MODE = 'SET_HIDDEN_MODE',
  SET_UNDERLINE_HIDDEN_MODE = 'SET_UNDERLINE_HIDDEN_MODE',
  SET_TOOLTIP = 'SET_TOOLTIP',
  SET_STATE = 'SET_STATE',
}

export const initialToolTState = {
  isVisible: false,
  word: {
    side: '',
    text: '',
    id: '',
    wordId: '',
    lineId: '',
    sectionId: '',
    wordIndex: 0,
    lineIndex: 0,
    sectionIndex: 0,
  },
  x: 0,
  y: 0,
};

const initialState: IWordState = {
  color: COLOR_PICKER[0],
  isHighlightingMode: false,
  isHideHighlightsMode: false,
  isFontMode: true,
  isStateSet: false,
  font: {},
  tool: initialToolTState,
  revealedSentences: [],
  images: [],
  imageTool: { isVisible: false, key: '' },
  isHiddenTextMode: false,
  isUnderlineHiddenMode: false,
};

export interface ITool {
  isVisible: boolean;
  word: ExemplarWord;
  x: number;
  y: number;
}

interface IHighlightedWord {
  [key: string]: {
    color: string;
    highlighted: boolean;
  };
}

export interface IHighlighted {
  [key: string]: IHighlightedWord;
}

export interface IWordState {
  color: string;
  isHighlightingMode: boolean;
  isHideHighlightsMode: boolean;
  isFontMode: boolean;
  isStateSet: boolean;
  font: IFont;
  tool: ITool;
  revealedSentences: Array<string>;
  images: Array<string | number>;
  imageTool: { isVisible: boolean; key: string | number };
  isHiddenTextMode: boolean;
  isUnderlineHiddenMode: boolean;
}

type payload = string | boolean | unknown;

export interface IAction {
  type: string;
  payload: payload;
}

export interface IFontStyle {
  text: string;
  style: string[];
  bold: boolean;
  italic: boolean;
  color: string;
  isDefault: boolean;
}

export interface IFont {
  [key: string]: { [key: string]: IFontStyle };
}

type IStyledWordContext = {
  styledWordState: typeof initialState;
  dispatch: (action: IAction) => void;
  handleHighlightingTool: () => void;
  handleColorPick: (color: string) => void;
  revealSentence: (id: string) => void;
  setOneRevealedSentence: (id: string) => void;
  handleHideHighlightsTool: () => void;
  handleTool: (isVisible: boolean, word: ExemplarWord, x: number, y: number) => void;
  handleImage: (id: string | number) => void;
  setImages: (ids: Array<string | number>) => void;
  handleImageTool: (isOpen: boolean) => void;
  handleHideText: (hide: boolean) => void;
  setUnderlineHidden: (isHidden: boolean) => void;
  clearStyledWords: () => void;
  handleChangeScreen: (state: IWordState) => void;
  saveHideHighlightsUpdates: (prevState: boolean, state: boolean) => void;
  saveImageHiddenUpdates: (prevState: Array<string | number>, state: Array<string | number>) => void;
  saveTextVisibilityUpdates: (prevState: boolean, state: boolean) => void;
};

// eslint-disable-next-line complexity
const wordReducer = (state: IWordState, action: IAction): IWordState => {
  switch (action.type) {
    case ActionTypes.SET_COLOR:
      return {
        ...state,
        color: action.payload as string,
      };
    case ActionTypes.SET_MODE:
      return {
        ...state,
        isHighlightingMode: action.payload as boolean,
      };
    case ActionTypes.SET_SENTENCES:
      return {
        ...state,
        revealedSentences: action.payload as Array<string>,
      };
    case ActionTypes.SET_HIDE_HIGHLIGHTS_MODE:
      return {
        ...state,
        isHideHighlightsMode: action.payload as boolean,
      };
    case ActionTypes.SET_FONT_COLOR:
      return {
        ...state,
        font: action.payload as IFont,
      };
    case ActionTypes.SET_FONT:
      return {
        ...state,
        font: action.payload as IFont,
      };
    case ActionTypes.SET_TEXT_COLOR:
      return {
        ...state,
        font: action.payload as IFont,
      };
    case ActionTypes.SET_TOOL:
      return {
        ...state,
        tool: action.payload as ITool,
      };
    case ActionTypes.SET_IMAGE:
      return {
        ...state,
        images: action.payload as Array<string | number>,
      };
    case ActionTypes.SET_IMAGE_TOOL:
      return {
        ...state,
        imageTool: action.payload as { isVisible: boolean; key: string | number },
      };
    case ActionTypes.SET_HIDDEN_MODE:
      return {
        ...state,
        isHiddenTextMode: action.payload as boolean,
      };
    case ActionTypes.SET_UNDERLINE_HIDDEN_MODE:
      return {
        ...state,
        isUnderlineHiddenMode: action.payload as boolean,
      };
    case ActionTypes.SET_STATE:
      return action.payload as IWordState;
    default:
      return state;
  }
};

export const StyledWordContext = createContext<IStyledWordContext>({
  styledWordState: initialState,
  dispatch: () => false,
  handleHighlightingTool: () => false,
  handleColorPick: () => false,
  revealSentence: () => false,
  setOneRevealedSentence: () => false,
  handleHideHighlightsTool: () => false,
  handleTool: () => false,
  handleImage: () => false,
  setImages: () => false,
  handleImageTool: () => false,
  handleHideText: () => false,
  setUnderlineHidden: () => null,
  clearStyledWords: () => false,
  handleChangeScreen: () => null,
  saveHideHighlightsUpdates: () => null,
  saveImageHiddenUpdates: () => null,
  saveTextVisibilityUpdates: () => null,
});

export const StyledWordContextProvider: React.FC = ({ children }): JSX.Element => {
  const [state, dispatch] = useReducer(wordReducer, initialState);
  const { registerUndoRedoCreator } = useContext(UndoRedoContext);

  const { page } = useParams();

  useEffect(() => {
    if (page === SENTENCES) {
      setOneRevealedSentence('left-1');
    }
  }, [page]);

  const handleChangeScreen = (state: IWordState) => {
    dispatch({ type: ActionTypes.SET_STATE, payload: state });
  };

  const handleHighlightingTool = () => {
    if (state.isHideHighlightsMode) {
      dispatch({ type: ActionTypes.SET_HIDE_HIGHLIGHTS_MODE, payload: false });
    }

    dispatch({ type: ActionTypes.SET_MODE, payload: !state.isHighlightingMode });
  };

  const handleHideHighlightsTool = (isActive?: boolean) => {
    if (state.isHighlightingMode) {
      dispatch({ type: ActionTypes.SET_MODE, payload: false });
    }

    isActive !== undefined
      ? dispatch({ type: ActionTypes.SET_HIDE_HIGHLIGHTS_MODE, payload: isActive })
      : dispatch({ type: ActionTypes.SET_HIDE_HIGHLIGHTS_MODE, payload: !state.isHideHighlightsMode });
  };

  const handleColorPick = (color: string) => {
    dispatch({ type: ActionTypes.SET_COLOR, payload: color });
  };

  const revealSentence = (id: string) => {
    const isRevealed = state.revealedSentences.find(sentenceId => sentenceId === id);

    if (isRevealed) {
      const sentences = state.revealedSentences.filter(sentenceId => sentenceId !== id);
      dispatch({ type: ActionTypes.SET_SENTENCES, payload: sentences });
      return;
    }

    const uniqueIds = [...new Set([...state.revealedSentences, id])];

    dispatch({ type: ActionTypes.SET_SENTENCES, payload: uniqueIds });
  };

  const setOneRevealedSentence = (id: string) => {
    dispatch({ type: ActionTypes.SET_SENTENCES, payload: [id] });
  };

  const handleTool = (isVisible: boolean, word: ExemplarWord, x: number, y: number) => {
    dispatch({
      type: ActionTypes.SET_TOOL,
      payload: {
        isVisible: isVisible,
        word: word,
        x: x,
        y: y,
      },
    });
  };

  const setImages = (ids: Array<string | number>) => {
    dispatch({ type: ActionTypes.SET_IMAGE, payload: ids });
  };

  const handleImage = (id: string | number) => {
    if (state.images.includes(id)) {
      const restImages = state.images.filter((image: string | number) => {
        return image !== id;
      });

      dispatch({ type: ActionTypes.SET_IMAGE, payload: [...restImages] });
    } else {
      dispatch({ type: ActionTypes.SET_IMAGE, payload: [...state.images, id] });
    }
  };

  const handleImageTool = (isOpen: boolean): void => {
    const payload = { ...state.imageTool, isVisible: isOpen };
    dispatch({ type: ActionTypes.SET_IMAGE_TOOL, payload: payload });
  };

  const handleHideText = (hide: boolean) => {
    dispatch({ type: ActionTypes.SET_HIDDEN_MODE, payload: hide });
  };

  const setUnderlineHidden = (isHidden: boolean) => {
    dispatch({ type: ActionTypes.SET_UNDERLINE_HIDDEN_MODE, payload: isHidden });
  };

  const clearStyledWords = () => {
    dispatch({
      type: ActionTypes.SET_STATE,
      payload: {
        ...initialState,
        revealedSentences: state.revealedSentences,
      },
    });
  };

  const saveHideHighlightsUpdates = registerUndoRedoCreator(Type.hideHighlights, (payload: UndoRedoAction) => {
    handleHideHighlightsTool(payload as boolean);
  });

  const saveImageHiddenUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.hideImage, (payload: UndoRedoAction) => {
        setImages(payload as Array<string | number>);
      }),
    []
  );

  const saveTextVisibilityUpdates = registerUndoRedoCreator(Type.hideText, (payload: UndoRedoAction) => {
    handleHideText(payload as boolean);
  });

  return (
    <StyledWordContext.Provider
      value={{
        dispatch,
        styledWordState: state,
        handleHighlightingTool,
        handleColorPick,
        revealSentence,
        setOneRevealedSentence,
        handleHideHighlightsTool,
        handleTool,
        handleImage,
        setImages,
        handleImageTool,
        handleHideText,
        setUnderlineHidden,
        clearStyledWords,
        handleChangeScreen,
        saveHideHighlightsUpdates,
        saveImageHiddenUpdates,
        saveTextVisibilityUpdates,
      }}
    >
      {children}
    </StyledWordContext.Provider>
  );
};
